<script setup>
import { ref, computed } from 'vue';
import { useToggle } from '@vueuse/core';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useVuelidate } from '@vuelidate/core';
import { required, email, minLength, helpers } from '@vuelidate/validators';
import Button from 'dashboard/components-next/button/Button.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';

const emit = defineEmits(['close']);

const store = useStore();
const { t } = useI18n();

const agentName = ref('');
const agentEmail = ref('');
const agentPhone = ref('');
const agentPassword = ref('');
const selectedRoleId = ref('agent');
const avatarFile = ref(null);
const avatarUrl = ref('');
const [isPasswordVisible, togglePasswordVisibility] = useToggle();

const chinaMobile = helpers.withMessage(
  () => t('AGENT_MGMT.ADD.FORM.PHONE_NUMBER.ERROR'),
  value => {
    const digits = String(value || '')
      .replace(/\D/g, '')
      .replace(/^86/, '');
    return /^1[3-9]\d{9}$/.test(digits);
  }
);

const rules = {
  agentName: { required },
  agentEmail: { required, email },
  agentPhone: { required, chinaMobile },
  agentPassword: {
    required,
    minLength: minLength(6),
    passwordComplexity: helpers.withMessage(
      () => t('AGENT_MGMT.ADD.FORM.PASSWORD.ERROR'),
      value =>
        /[A-Z]/.test(value) &&
        /[a-z]/.test(value) &&
        /\d/.test(value) &&
        /[^A-Za-z0-9]/.test(value)
    ),
  },
  selectedRoleId: { required },
};

const v$ = useVuelidate(rules, {
  agentName,
  agentEmail,
  agentPhone,
  agentPassword,
  selectedRoleId,
});

const uiFlags = useMapGetter('agents/getUIFlags');
const getCustomRoles = useMapGetter('customRole/getCustomRoles');

const roles = computed(() => {
  const defaultRoles = [
    {
      id: 'administrator',
      name: 'administrator',
      label: t('AGENT_MGMT.AGENT_TYPES.ADMINISTRATOR'),
    },
    {
      id: 'agent',
      name: 'agent',
      label: t('AGENT_MGMT.AGENT_TYPES.AGENT'),
    },
  ];

  const customRoles = getCustomRoles.value.map(role => ({
    id: role.id,
    name: `custom_${role.id}`,
    label: role.name,
  }));

  return [...defaultRoles, ...customRoles];
});

const selectedRole = computed(() =>
  roles.value.find(
    role =>
      role.id === selectedRoleId.value || role.name === selectedRoleId.value
  )
);

const buildAgentPayload = () => {
  const payload = {
    name: agentName.value,
    email: agentEmail.value,
    phone_number: agentPhone.value,
    password: agentPassword.value,
  };

  if (selectedRole.value.name.startsWith('custom_')) {
    payload.custom_role_id = selectedRole.value.id;
  } else {
    payload.role = selectedRole.value.name;
  }

  return payload;
};

const buildAvatarFormData = () => {
  const formData = new FormData();
  formData.append('agent[avatar]', avatarFile.value);
  return formData;
};

const handleAvatarUpload = ({ file, url }) => {
  avatarFile.value = file;
  avatarUrl.value = url;
};

const handleAvatarDelete = () => {
  avatarFile.value = null;
  avatarUrl.value = '';
};

const addAgent = async () => {
  v$.value.$touch();
  if (v$.value.$invalid) return;

  try {
    const createdAgent = await store.dispatch(
      'agents/create',
      buildAgentPayload()
    );

    if (avatarFile.value && createdAgent?.id) {
      await store.dispatch('agents/update', {
        id: createdAgent.id,
        formData: buildAvatarFormData(),
      });
    }

    useAlert(t('AGENT_MGMT.ADD.API.SUCCESS_MESSAGE'));
    emit('close');
  } catch (error) {
    const {
      response: {
        data: {
          error: errorResponse = '',
          attributes: attributes = [],
          message: attrError = '',
        } = {},
      } = {},
    } = error;

    let errorMessage = '';
    if (error?.response?.status === 422 && !attributes.includes('base')) {
      errorMessage = t('AGENT_MGMT.ADD.API.EXIST_MESSAGE');
    } else {
      errorMessage = t('AGENT_MGMT.ADD.API.ERROR_MESSAGE');
    }
    useAlert(errorResponse || attrError || errorMessage);
  }
};
</script>

<template>
  <div class="flex flex-col h-auto overflow-auto">
    <woot-modal-header
      :header-title="$t('AGENT_MGMT.ADD.TITLE')"
      :header-content="$t('AGENT_MGMT.ADD.DESC')"
    />
    <form class="flex flex-col items-start w-full" @submit.prevent="addAgent">
      <div class="w-full mb-4">
        <span class="text-sm font-medium text-n-slate-12">
          {{ $t('AGENT_MGMT.ADD.FORM.AVATAR.LABEL') }}
        </span>
        <div class="mt-2">
          <Avatar
            :src="avatarUrl || ''"
            :name="agentName || ''"
            :size="72"
            allow-upload
            @upload="handleAvatarUpload"
            @delete="handleAvatarDelete"
          />
        </div>
      </div>

      <div class="w-full">
        <label :class="{ error: v$.agentName.$error }">
          {{ $t('AGENT_MGMT.ADD.FORM.NAME.LABEL') }}
          <input
            v-model="agentName"
            type="text"
            :placeholder="$t('AGENT_MGMT.ADD.FORM.NAME.PLACEHOLDER')"
            @input="v$.agentName.$touch"
          />
        </label>
      </div>

      <div class="w-full">
        <label :class="{ error: v$.selectedRoleId.$error }">
          {{ $t('AGENT_MGMT.ADD.FORM.AGENT_TYPE.LABEL') }}
          <select v-model="selectedRoleId" @change="v$.selectedRoleId.$touch">
            <option v-for="role in roles" :key="role.id" :value="role.id">
              {{ role.label }}
            </option>
          </select>
          <span v-if="v$.selectedRoleId.$error" class="message">
            {{ $t('AGENT_MGMT.ADD.FORM.AGENT_TYPE.ERROR') }}
          </span>
        </label>
      </div>

      <div class="w-full">
        <label :class="{ error: v$.agentEmail.$error }">
          {{ $t('AGENT_MGMT.ADD.FORM.EMAIL.LABEL') }}
          <input
            v-model="agentEmail"
            type="email"
            :placeholder="$t('AGENT_MGMT.ADD.FORM.EMAIL.PLACEHOLDER')"
            @input="v$.agentEmail.$touch"
          />
        </label>
      </div>

      <div class="w-full">
        <label :class="{ error: v$.agentPhone.$error }">
          {{ $t('AGENT_MGMT.ADD.FORM.PHONE_NUMBER.LABEL') }}
          <input
            v-model="agentPhone"
            type="tel"
            maxlength="13"
            :placeholder="$t('AGENT_MGMT.ADD.FORM.PHONE_NUMBER.PLACEHOLDER')"
            @input="v$.agentPhone.$touch"
          />
          <span v-if="v$.agentPhone.$error" class="message">
            {{ $t('AGENT_MGMT.ADD.FORM.PHONE_NUMBER.ERROR') }}
          </span>
          <span v-else class="text-xs text-n-slate-10">
            {{ $t('AGENT_MGMT.ADD.FORM.PHONE_NUMBER.HELP') }}
          </span>
        </label>
      </div>

      <div class="w-full">
        <label :class="{ error: v$.agentPassword.$error }">
          {{ $t('AGENT_MGMT.ADD.FORM.PASSWORD.LABEL') }}
          <div class="relative">
            <input
              v-model="agentPassword"
              :type="isPasswordVisible ? 'text' : 'password'"
              autocomplete="new-password"
              class="pr-10"
              :placeholder="$t('AGENT_MGMT.ADD.FORM.PASSWORD.PLACEHOLDER')"
              @input="v$.agentPassword.$touch"
            />
            <Button
              type="button"
              slate
              sm
              link
              :icon="isPasswordVisible ? 'i-lucide-eye-off' : 'i-lucide-eye'"
              class="absolute inset-y-0 right-0 pr-3"
              :aria-label="
                isPasswordVisible
                  ? $t('AGENT_MGMT.ADD.FORM.PASSWORD.HIDE')
                  : $t('AGENT_MGMT.ADD.FORM.PASSWORD.SHOW')
              "
              :aria-pressed="isPasswordVisible"
              @click="togglePasswordVisibility()"
            />
          </div>
          <span v-if="v$.agentPassword.$error" class="message">
            {{ $t('AGENT_MGMT.ADD.FORM.PASSWORD.ERROR') }}
          </span>
          <span v-else class="text-xs text-n-slate-10">
            {{ $t('AGENT_MGMT.ADD.FORM.PASSWORD.HELP') }}
          </span>
        </label>
      </div>

      <div class="flex flex-row justify-end w-full gap-2 px-0 py-2">
        <Button
          faded
          slate
          type="reset"
          :label="$t('AGENT_MGMT.ADD.CANCEL_BUTTON_TEXT')"
          @click.prevent="emit('close')"
        />
        <Button
          type="submit"
          :label="$t('AGENT_MGMT.ADD.FORM.SUBMIT')"
          :disabled="v$.$invalid || uiFlags.isCreating"
          :is-loading="uiFlags.isCreating"
        />
      </div>
    </form>
  </div>
</template>
